import React, { useState } from "react";
import { Button, Modal } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

/**
 * Icono (i) que abre una explicación de cómo funciona una función. Es un modal
 * (no un tooltip) a propósito: el tooltip depende del hover, que en el celular
 * no existe, y las explicaciones de esta app tienen varios párrafos.
 *
 * Uso:
 *   <InfoAyuda titulo="Almacenamiento">
 *     <AyudaBloque titulo="Qué ves">…</AyudaBloque>
 *   </InfoAyuda>
 */
export interface InfoAyudaProps {
  /** Título del modal; también forma la etiqueta accesible del botón. */
  titulo: string;
  /** Contenido: párrafos, listas o varios `<AyudaBloque>`. */
  children: React.ReactNode;
  /** Texto corto visible junto al icono (p. ej. «¿Cómo funciona?»). Por defecto solo el icono. */
  texto?: string;
  /** Para pruebas. El modal usa `${testId}-modal`. */
  testId?: string;
  /** Clase extra del botón (p. ej. para integrarlo sobre un video). */
  className?: string;
  /** Estilo del botón (p. ej. alto mínimo táctil). */
  style?: React.CSSProperties;
}

const detener = (e: React.SyntheticEvent) => e.stopPropagation();

const InfoAyuda: React.FC<InfoAyudaProps> = ({
  titulo,
  children,
  texto,
  testId = "info-ayuda",
  className,
  style,
}) => {
  const [abierto, setAbierto] = useState(false);

  return (
    // El modal vive en un portal, pero sus eventos suben por el árbol de React hasta los
    // ancestros (una fila clicable, un carrusel, otro modal que cierra con Esc). Este envoltorio
    // sin caja (display: contents) los corta para que leer la ayuda no active nada detrás.
    <span style={{ display: "contents" }} onClick={detener} onKeyDown={detener}>
      <Button
        type="text"
        size="small"
        className={className ? `info-ayuda-btn ${className}` : "info-ayuda-btn"}
        style={style}
        icon={<InfoCircleOutlined />}
        aria-label={`¿Cómo funciona? ${titulo}`}
        title="¿Cómo funciona?"
        data-testid={testId}
        onClick={(e) => {
          // El icono suele vivir dentro de encabezados, filas o pestañas clicables.
          e.stopPropagation();
          setAbierto(true);
        }}
      >
        {texto}
      </Button>
      <Modal
        open={abierto}
        title={titulo}
        onCancel={() => setAbierto(false)}
        footer={
          <Button type="primary" onClick={() => setAbierto(false)} data-testid={`${testId}-cerrar`}>
            Entendido
          </Button>
        }
        width={560}
        style={{ maxWidth: "calc(100vw - 24px)", top: 24 }}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
        destroyOnClose
        data-testid={`${testId}-modal`}
      >
        <div className="info-ayuda-contenido">{children}</div>
      </Modal>
    </span>
  );
};

/** Un apartado de la explicación: subtítulo en negrita y su contenido. */
export const AyudaBloque: React.FC<{ titulo: string; children: React.ReactNode }> = ({
  titulo,
  children,
}) => (
  <section style={{ marginBottom: 14 }}>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>{titulo}</div>
    <div style={{ fontSize: 14, lineHeight: 1.5 }}>{children}</div>
  </section>
);

export default InfoAyuda;
